"use client";

import { useState, useCallback, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAVIGATION, SITE_CONFIG } from "@/lib/constants";
import { Wordmark } from "@/components/layout/wordmark";

interface HeaderProps {
  mobileMenuOpen?: boolean;
  onMobileMenuChange?: (open: boolean) => void;
}

export function Header({ mobileMenuOpen: controlledOpen, onMobileMenuChange }: HeaderProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const pathname = usePathname();

  // Support both controlled and uncontrolled usage
  const mobileMenuOpen = controlledOpen ?? internalOpen;
  const setMobileMenuOpen = onMobileMenuChange ?? setInternalOpen;

  const toggleMenu = useCallback(() => {
    setMobileMenuOpen(!mobileMenuOpen);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  const closeMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, [setMobileMenuOpen]);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMenu(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (mobileMenuOpen) closeMenu();
        setOpenMenu(null);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileMenuOpen, closeMenu]);

  return (
    <>
      <a className="skip-to-content" href="#main-content">
        Skip to content
      </a>

      <header className="relative z-50 border-b border-line bg-parchment/95 backdrop-blur supports-[backdrop-filter]:bg-parchment/80">
        <div className="mx-auto flex h-[80px] max-w-[1280px] items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            href="/"
            className="group rounded-sm transition-opacity hover:opacity-80"
            aria-label="tutorials.co.uk - Home"
          >
            <Wordmark />
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex lg:items-center lg:gap-2" aria-label="Main navigation">
            {NAVIGATION.main.map((item) => {
              const children = "children" in item ? item.children : undefined;
              const isActive =
                pathname === item.href ||
                pathname.startsWith(`${item.href}/`) ||
                (children?.some(
                  (c) =>
                    pathname === c.href || pathname.startsWith(`${c.href}/`),
                ) ??
                  false);

              if (children) {
                const open = openMenu === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setOpenMenu(item.label)}
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <button
                      type="button"
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-primary",
                        isActive ? "text-primary" : "text-dark/75",
                      )}
                      aria-haspopup="true"
                      aria-expanded={open}
                      onClick={() =>
                        setOpenMenu((m) => (m === item.label ? null : item.label))
                      }
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          "size-4 transition-transform",
                          open && "rotate-180",
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    {open && (
                      <div className="absolute left-0 top-full z-50 w-72 rounded-md border border-line bg-paper p-2 shadow-lg">
                        {children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            className="block rounded-md px-3 py-2 transition-colors hover:bg-light-bg"
                            onClick={() => setOpenMenu(null)}
                          >
                            <span className="block text-sm font-semibold text-dark">
                              {child.label}
                            </span>
                            <span className="block text-xs text-mid">
                              {child.description}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "rounded-md px-3 py-2 text-sm font-medium underline-offset-[6px] transition-colors",
                    "hover:text-primary hover:underline hover:decoration-secondary",
                    isActive
                      ? "text-primary underline decoration-secondary decoration-2"
                      : "text-dark/75",
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex lg:items-center lg:gap-5">
            <a
              href={`tel:${SITE_CONFIG.parentCompany.phone.replace(/\s+/g, "")}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-dark/75 transition-colors hover:text-primary"
              aria-label={`Call us free on ${SITE_CONFIG.parentCompany.phone}`}
            >
              <Phone className="size-4" aria-hidden="true" />
              {SITE_CONFIG.parentCompany.phone}
            </a>
            <Link
              href="/login"
              className="text-sm font-medium text-dark/75 transition-colors hover:text-primary"
            >
              Sign in
            </Link>
            <Link
              href="/train-your-team"
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-paper transition-colors hover:bg-primary-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            >
              Get your team started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-dark transition-colors hover:bg-primary/[0.06] hover:text-primary lg:hidden"
            onClick={toggleMenu}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu-overlay"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile menu overlay. Rendered OUTSIDE <header>: the header's
          backdrop-filter would otherwise act as the containing block for this
          position:fixed overlay, trapping it inside the 80px header box and
          leaving the page visible behind the menu. */}
      {mobileMenuOpen && (
        <div
          id="mobile-menu-overlay"
          className="fixed inset-0 z-[60] bg-parchment lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
        >
          <div className="flex h-full flex-col">
            {/* Overlay header */}
            <div className="flex h-[80px] items-center justify-between border-b border-line px-4 sm:px-6">
              <Link
                href="/"
                className="group"
                onClick={closeMenu}
                aria-label="tutorials.co.uk - Home"
              >
                <Wordmark small />
              </Link>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-dark transition-colors hover:bg-primary/[0.06] hover:text-primary"
                onClick={closeMenu}
                aria-label="Close menu"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Overlay navigation */}
            <nav className="flex-1 overflow-y-auto px-4 py-6 sm:px-6" aria-label="Mobile navigation">
              <ul className="space-y-1">
                {NAVIGATION.main.map((item) => {
                  const children = "children" in item ? item.children : undefined;
                  if (children) {
                    return (
                      <li key={item.label} className="pt-2">
                        <span className="block px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-mid">
                          {item.label}
                        </span>
                        <ul>
                          {children.map((child) => {
                            const active =
                              pathname === child.href ||
                              pathname.startsWith(`${child.href}/`);
                            return (
                              <li key={child.href}>
                                <Link
                                  href={child.href}
                                  className={cn(
                                    "flex min-h-[44px] items-center rounded-md px-4 py-3 text-base font-medium transition-colors hover:bg-primary/[0.06] hover:text-primary",
                                    active
                                      ? "bg-primary/[0.06] text-primary"
                                      : "text-dark/80",
                                  )}
                                  onClick={closeMenu}
                                  aria-current={active ? "page" : undefined}
                                >
                                  {child.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  }
                  const isActive =
                    pathname === item.href ||
                    pathname.startsWith(`${item.href}/`);
                  return (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        className={cn(
                          "flex min-h-[44px] items-center rounded-md px-4 py-3 text-base font-medium transition-colors",
                          "hover:bg-primary/[0.06] hover:text-primary",
                          isActive
                            ? "bg-primary/[0.06] text-primary"
                            : "text-dark/80",
                        )}
                        onClick={closeMenu}
                        aria-current={isActive ? "page" : undefined}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Overlay actions */}
            <div className="border-t border-line px-4 py-6 sm:px-6">
              <Link
                href="/train-your-team"
                className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-primary px-4 py-3 text-base font-semibold text-paper transition-colors hover:bg-primary-dark"
                onClick={closeMenu}
              >
                Get your team started
              </Link>
              <Link
                href="/login"
                className="mt-3 flex min-h-[44px] w-full items-center justify-center rounded-md border border-line px-4 py-3 text-base font-medium text-dark/80 transition-colors hover:border-primary hover:text-primary"
                onClick={closeMenu}
              >
                Sign in
              </Link>
              <a
                href={`tel:${SITE_CONFIG.parentCompany.phone.replace(/\s+/g, "")}`}
                className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-base font-medium text-dark/80 transition-colors hover:text-primary"
              >
                <Phone className="size-4" aria-hidden="true" />
                Call us free: {SITE_CONFIG.parentCompany.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
